import { invokeWithRetry } from "./common/invokeWithRetry";
import i18n from "@/i18n";

const FUNCTION_NAME = "trial-prospects";

export type TrialStatus = "inscrit" | "non_inscrit" | "en_attente" | "absence";

export interface TrialProspect {
  id: string;
  gym_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  class_id: string;
  class_date: string;
  time: string | null;
  status: TrialStatus;
  converted_client_id: string | null;
  converted_at: string | null;
  notes: string | null;
  source: string | null;
  clothing_size: string | null;
  created_at: string;
  updated_at: string;
  scheduled_classes?: {
    id: string;
    name: string;
    time: string | null;
    instructor: string | null;
  } | null;
}

export interface TrialStats {
  total: number;
  counts: { inscrit: number; non_inscrit: number; en_attente: number; absence: number };
  conversion_rate: number;
}

export interface TrialSlot {
  id: string;
  name: string;
  class_date: string;
  time: string;
  capacity: number;
  booked: number;
  trials: number;
  available: number;
}

async function call<T = any>(body: Record<string, unknown>): Promise<T> {
  const { data, error, authExpired } = await invokeWithRetry(FUNCTION_NAME, body);
  if (authExpired) throw new Error(i18n.t("common:errors.sessionExpired"));
  if (error) throw new Error(error.message ?? i18n.t("common:errors.trialProspectsError"));
  if (data && (data as any).error) throw new Error((data as any).message ?? (data as any).error);
  return data as T;
}

export const trialProspectsApi = {
  list(gym_id: string, filters: { status?: TrialStatus; start_date?: string; end_date?: string; class_id?: string; search?: string } = {}) {
    return call<{ success: boolean; data: TrialProspect[] }>({ action: "list", gym_id, ...filters }).then(r => r.data ?? []);
  },
  create(gym_id: string, payload: { first_name: string; last_name: string; email: string; phone?: string; class_id: string; class_date: string; notes?: string; source?: string; clothing_size?: string }) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "create", gym_id, ...payload }).then(r => r.data);
  },
  update(gym_id: string, payload: { id: string; first_name?: string; last_name?: string; email?: string; phone?: string; notes?: string; source?: string; clothing_size?: string }) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "update", gym_id, ...payload }).then(r => r.data);
  },
  updateStatus(gym_id: string, id: string, status: TrialStatus) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "update_status", gym_id, id, status }).then(r => r.data);
  },
  remove(gym_id: string, id: string) {
    return call<{ success: boolean }>({ action: "delete", gym_id, id });
  },
  convert(gym_id: string, id: string, opts: { subscription_type_id?: string; app_name?: string } = {}) {
    return call<{ success: boolean; data: TrialProspect; user_id: string }>({ action: "convert", gym_id, id, ...opts });
  },
  stats(gym_id: string, range: { start_date?: string; end_date?: string } = {}) {
    return call<{ success: boolean; data: TrialStats }>({ action: "stats", gym_id, ...range }).then(r => r.data);
  },
  listSlots(gym_id: string, start_date: string, end_date: string) {
    return call<{ success: boolean; data: TrialSlot[] }>({ action: "list_slots", gym_id, start_date, end_date }).then(r => r.data ?? []);
  },
  listByClasses(gym_id: string, class_ids: string[], class_date: string) {
    return call<{ success: boolean; data: Array<Pick<TrialProspect, 'id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'class_id' | 'class_date' | 'time' | 'status' | 'notes' | 'clothing_size'> & { coach_id: string | null; coach_first_name: string | null; coach_last_name: string | null }> }>({
      action: "list_by_classes", gym_id, class_ids, class_date,
    }).then(r => r.data ?? []);
  },
  assignCoach(gym_id: string, id: string, coach_id: string) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "assign_coach", gym_id, id, coach_id }).then(r => r.data);
  },
  unassignCoach(gym_id: string, id: string) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "unassign_coach", gym_id, id }).then(r => r.data);
  },
  cancel(gym_id: string, id: string) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "cancel", gym_id, id }).then(r => r.data);
  },
  reschedule(gym_id: string, id: string, new_class_id: string, new_class_date: string) {
    return call<{ success: boolean; data: TrialProspect }>({ action: "reschedule", gym_id, id, new_class_id, new_class_date }).then(r => r.data);
  },


};
